<template>
    <div class="google-signin-wrapper">
        <button class="sign-btn btn-secondary"
                @click="handleGoogleSignIn"
                :disabled="loading">
            <img    src="@/assets/google-logo.png"
                    :alt="$t('auth.google.logoAlt')"
                    class="sign-btn-logo">
            {{ loading ? $t('auth.google.signingIn') : $t('auth.google.signIn') }}
        </button>
        <div v-if="error" class="google-error">{{ error }}</div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { authAPI } from '@/services/api';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const store = useStore();
const loading = ref(false);
const error = ref('');
const { t } = useI18n();

function initializeGoogle() {
    if (!window.google?.accounts?.id) return;

    window.google.accounts.id.initialize({
        client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
    });
}

onMounted(() => {
    if (window.google?.accounts?.id) {
        initializeGoogle();
    } else {
        // GIS script may still be loading (async defer)
        const interval = setInterval(() => {
            if (window.google?.accounts?.id) {
                clearInterval(interval);
                initializeGoogle();
            }
        }, 100);
        // Stop checking after 10s
        setTimeout(() => clearInterval(interval), 10000);
    }
});

function handleGoogleSignIn() {
    error.value = '';
    if (window.google?.accounts?.id) {
        window.google.accounts.id.prompt();
    } else {
        error.value = t('auth.google.unavailableSignIn');
    }
}

async function handleCredentialResponse(response) {
    loading.value = true;
    error.value = '';

    try {
        const result = await authAPI.googleAuth(response.credential);
        const sessionId = result.session_id;
        const userFolderPath = result.user_folder_path;

        store.dispatch('login', { sessionId, userFolderPath, email: '' });
        router.push('/app');
    } catch (err) {
        console.error('Google Sign-In error:', err);
        error.value = err.message || t('auth.google.failedSignIn');
    } finally {
        loading.value = false;
    }
}
</script>

<style scoped>
    .google-signin-wrapper {
        width: 100%;
    }

    .sign-btn {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px 20px;
        background-color: rgba(255, 255, 255, .1);
        border-radius: 8px;
        width: 100%;
        cursor: pointer;
    }

    .sign-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .sign-btn-logo {
        padding-right: 5px;
    }

    .google-error {
        color: var(--color-error);
        text-align: center;
        margin-top: var(--spacing-sm);
        font-size: var(--font-size-description);
    }
</style>
